import { useEffect, useState } from "react";
import { IconSearch } from "@tabler/icons-react";
import type { ProductDto } from "../../../../api";
import { Dialog } from "../../../../components/common/dialog";
import { EmptyState } from "../../../../components/common/empty-state";
import { Image } from "../../../../components/common/image";
import { SearchBar } from "../../../../components/common/input";
import { PreviewListRow } from "../../../../components/common/preview-list-row";
import { Shimmer, ShimmerLoader } from "../../../../components/common/shimmer";
import { useProducts } from "../../hooks/useProducts";

const SEARCH_DEBOUNCE_MS = 300;
const RESULTS_PAGE_SIZE = 8;

interface AddToComparisonDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called with the catalog product the user picked; the dialog then closes. */
  onSelect: (product: ProductDto) => void;
  /** Product template ids already in the comparison — left out of the results. */
  excludeProductIds: ReadonlySet<number>;
}

/**
 * Catalog search for adding one more product to the compare tray, so the
 * tray can grow past whatever the current page happens to list (e.g. a
 * wishlist with a single saved product).
 */
export function AddToComparisonDialog({
  open,
  onOpenChange,
  onSelect,
  excludeProductIds,
}: AddToComparisonDialogProps) {
  return (
    <Dialog
      open={open}
      setOpen={onOpenChange}
      title="Add a product to compare"
      titleAlignment="left"
      showCloseButton
      showButtons={false}
      width="max-w-xl"
      content={
        // Mounted only while the dialog is open, so the catalog isn't fetched
        // for every page that renders the compare tray.
        <ProductSearchResults
          excludeProductIds={excludeProductIds}
          onSelect={(product) => {
            onSelect(product);
            onOpenChange(false);
          }}
        />
      }
    />
  );
}

function ProductSearchResults({
  excludeProductIds,
  onSelect,
}: Pick<AddToComparisonDialogProps, "excludeProductIds" | "onSelect">) {
  const [searchValue, setSearchValue] = useState("");
  const [search, setSearch] = useState("");
  // Snapshot on open: picking a product adds it to the caller's exclusions
  // while the dialog is still fading out, which would flash "No products found".
  const [excluded] = useState(excludeProductIds);

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchValue.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchValue]);

  const { data, isLoading } = useProducts({
    page: 1,
    pageSize: RESULTS_PAGE_SIZE,
    ...(search && { search }),
  });
  const products = (data?.data ?? []).filter((p) => !excluded.has(p.id));

  return (
    <div className="flex flex-col gap-4">
      <SearchBar
        value={searchValue}
        onChange={(e) => setSearchValue(e.target.value)}
        placeholder="Search the catalog"
        autoFocus
      />
      {isLoading ? (
        <ShimmerLoader message="Searching the catalog…">
          <div className="flex flex-col gap-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Shimmer key={i} className="h-[74px] w-full" />
            ))}
          </div>
        </ShimmerLoader>
      ) : products.length === 0 ? (
        <EmptyState
          icon={IconSearch}
          size="sm"
          title="No products found"
          description={search ? "Try a different search term." : undefined}
          className="!py-4"
        />
      ) : (
        <div className="flex flex-col gap-3">
          {products.map((product) => (
            <PreviewListRow
              key={product.id}
              thumbnail={
                <Image
                  image={product.image}
                  alt=""
                  size="w-full h-full"
                  showPlaceholder
                />
              }
              title={product.name}
              subtitle={product.category?.name ?? undefined}
              actionLabel="Add"
              onClick={() => onSelect(product)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
